import React from 'react';
import { useIntl } from 'react-intl';
import { CHART_COLORS, BLACK, WHITE } from 'shared/constants/colors';
import theme from 'shared/constants/theme.json';
import { round } from 'shared/constants/math';
import Chart, { VERTICAL_LEGEND_THRESHOLD } from './Chart';
import { colorChartParts, mergeValues, iconBase } from './utils';
import messages from './messages';
import { formatSegment } from './formatters';

const PIE_ROW_HEIGHT = 280;
const MAX_PIE_RADIUS = 90;

// It is the smallest innerPieRadius on desktop
const MIN_INNER_PIE_RADIUS_ON_DESKTOP = 29;

const getPieRadius = (piesPerRow, width) => Math.min(MAX_PIE_RADIUS, (width / piesPerRow) * 0.25);
const coordToPercent = (i, percentToFill) => (i * percentToFill) + (percentToFill / 2);

const PieChart = (props) => {
  const {
    segments,
    question,
    scoreType,
    isPrint,
    isScoreGraph,
    chartSettings,
  } = props;

  const { formatMessage, locale } = useIntl();

  const { displayMode } = chartSettings;

  const tooltipFormatter = ({ name, value, percent, seriesName }) => {
    // Beacuse `seriesName` in the pie options is => name: `pie-${index}` || name: `doughnut-${index}`
    const withDoughnutTooltip = (seriesName.split('-')[0] === 'doughnut');
    const index = seriesName.split('-')[1];
    const segment = segments[index];
    const formattedSegment = formatSegment(segment, { lineBreak: '<br />', formatMessage, locale, ...props });

    if (withDoughnutTooltip) {
      return (segment && formattedSegment) || null;
    }

    return `${name}: ${value} (${round(percent, 1)}%)<br />${formattedSegment}`;
  };

  const labelFormatter = ({ value, percent = 0 }) => {
    if (displayMode !== 'percentage' || isScoreGraph) {
      return value;
    }
    return `${round(percent, 0)}%`;
  };

  const legendData = [];
  const series = [];
  const title = [];
  let selected;

  const pieSegments = segments.map(s => {
    if (s.score != null && !selected) {
      selected = s.values
        .filter(value => value.optionValue === null)
        .reduce((acc, value) => ({ ...acc, [value.name]: false }), {});
    }

    const data = colorChartParts(question, mergeValues(s, question, formatMessage));

    data.forEach(v => {
      if (!legendData.includes(v.name)) {
        legendData.push(v.name);
      }
    });

    return {
      ...s,
      data,
    };
  });

  const isVerticalLegend = legendData.length >= VERTICAL_LEGEND_THRESHOLD;

  const legend = {
    data: legendData,
    selected,
  };

  let maxPiesPerRow = 3;
  let { width } = props;

  if (isVerticalLegend) {
    width -= isPrint ? 100 : 200;
  }

  if (isPrint) {
    maxPiesPerRow = 2;
  }

  const rowCount = Math.ceil(segments.length / maxPiesPerRow);
  const piesPerRow = Math.ceil(segments.length / rowCount);
  const radius = getPieRadius(piesPerRow, isVerticalLegend ? width - 200 : width);
  const columnWidthInPercent = Math.round((1 / piesPerRow) * 100);

  const getPieCenterCoords = (pieIndex) => {
    const column = pieIndex % piesPerRow;
    const x = coordToPercent(column, columnWidthInPercent) * (isVerticalLegend ? 0.80 : 1); // make room for legend to the right

    const row = Math.floor((pieIndex / piesPerRow));
    const y = row * PIE_ROW_HEIGHT + PIE_ROW_HEIGHT / 2 - 10;

    return [`${x}%`, y];
  };

  pieSegments.forEach((segment, index) => {
    const hasScore = segment.score != null;
    const isEmpty = !hasScore && !segment.data.length;
    const isDoughnut = hasScore || isEmpty;
    const center = getPieCenterCoords(index);

    // => '{icon|} Segment 1 (100) \n 2019 - 2020
    const subtext = `{icon|} ${formatSegment(segment, { formatMessage, locale })}`;

    title.push({
      subtext,
      top: center[1],
      left: center[0],
      padding: [radius + 25, 0, 0, 0],
      subtextStyle: {
        color: BLACK,
        align: 'center',
        lineHeight: 16,
        rich: {
          icon: {
            ...iconBase,
            backgroundColor: segment.color || CHART_COLORS[index],
          },
        },
      },
    });

    const innerPieRadius = radius * 0.7;

    if (isDoughnut) {
      // If score exist, ADD a `doughnut` pie in the center of the originial pie
      const innerPieSubtext = messages[scoreType] && innerPieRadius >= MIN_INNER_PIE_RADIUS_ON_DESKTOP
        ? formatMessage(messages[scoreType])
        : null;

      const formatter = isEmpty ? `{subText|${formatMessage(messages.noMatchingData)}}` : `{heading|${segment.score}}\n{subText|${innerPieSubtext}}`;

      const headingFontSize = innerPieRadius >= MIN_INNER_PIE_RADIUS_ON_DESKTOP
        ? parseInt(theme.fontSizeBigger, 10)
        : parseInt(theme.fontSizeBig, 10);

      series.push({
        type: 'pie',
        name: `doughnut-${index}`,
        radius: [innerPieRadius * 0.8, innerPieRadius],
        center,
        color: segment.color,
        itemStyle: {
          verticalAlign: 'middle',
          color: segment.color,
        },
        label: {
          formatter,
          position: 'center',
          rich: {
            heading: {
              fontFamily: 'Nunito, Helvetica Neue, sans-serif',
              fontSize: headingFontSize,
              lineHeight: headingFontSize,
              fontWeight: '800',
              color: BLACK,
            },
            subText: {
              fontFamily: 'Nunito, Helvetica Neue, sans-serif',
              fontSize: parseInt(theme.fontSizeSmallest, 10),
              lineHeight: parseInt(theme.fontSizeSmallest, 10),
              color: BLACK,
            },
          },
        },
        data: [{ value: index + 1, name: '' }],
      });
    }

    series.push({
      type: 'pie',
      name: `pie-${index}`,
      radius: hasScore ? [radius * 0.75, radius] : radius,
      center,
      selectedMode: 'multiple',
      itemStyle: {
        borderWidth: 1,
        borderColor: WHITE,
      },
      label: {
        color: BLACK,
        formatter: labelFormatter,
      },
      data: segment.data,
    });
  });

  const height = rowCount * PIE_ROW_HEIGHT + 20;

  const option = {
    title,
    textAlign: 'center',
    tooltip: {
      trigger: 'item',
      formatter: tooltipFormatter,
    },
    legend,
    series,
  };

  return (
    <Chart
      {...props}
      height={height}
      option={option}
    />
  );
};

export default PieChart;
